/* eslint-disable @typescript-eslint/no-explicit-any, no-console */
import type { SiteLanguageCode } from '@yoyoclub/shared';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Generic API request function with authentication
 */
export async function apiRequest<T = any>(endpoint: string, options: RequestInit = {}): Promise<T> {
  try {
    // Get authentication token
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    // Prepare headers
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    // Make request
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return {} as T;
    }

    const data = await response.json();

    // Handle error responses
    if (!response.ok) {
      throw new Error(data.error || data.message || `HTTP ${response.status}`);
    }

    return data;
  } catch (error: any) {
    console.error('API request failed:', error);
    throw error;
  }
}

/**
 * Auth API endpoints
 */
export const authApi = {
  /**
   * Register user in database after Cognito signup
   * cognitoId and email are taken from JWT token server-side
   */
  register: (data: { name: string }) =>
    apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Get current user profile from database
   */
  getCurrentUser: () => apiRequest('/auth/me'),

  /** Saved site language (docs/prd-bilingual-content.md §5.6). */
  getPreferences: () =>
    apiRequest<{ preferredLanguage: SiteLanguageCode | null }>('/auth/me/preferences'),

  /**
   * Update user profile
   */
  updateProfile: (data: { name?: string; phone?: string; preferredLanguage?: SiteLanguageCode }) =>
    apiRequest('/auth/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /**
   * Logout
   */
  logout: () =>
    apiRequest('/auth/logout', {
      method: 'POST',
    }),
};

/**
 * Activities API endpoints
 */
export const activitiesApi = {
  /**
   * Get all activities
   */
  getAll: () => apiRequest('/activities'),

  /**
   * Get activity by ID
   */
  getById: (id: string) => apiRequest(`/activities/${id}`),

  /**
   * Create new activity (Host/Admin only)
   */
  create: (data: any) =>
    apiRequest('/activities', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Update activity (Host/Admin only)
   */
  update: (id: string, data: any) =>
    apiRequest(`/activities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /**
   * Delete activity (Host/Admin only)
   */
  delete: (id: string) =>
    apiRequest(`/activities/${id}`, {
      method: 'DELETE',
    }),
};

/**
 * Bookings API endpoints
 */
export const bookingsApi = {
  /**
   * Get all bookings (Admin only)
   */
  getAll: () => apiRequest('/bookings'),

  /**
   * Get user's bookings
   */
  getMine: () => apiRequest('/bookings/mine'),

  /**
   * Get booking by ID
   */
  getById: (id: string) => apiRequest(`/bookings/${id}`),

  /**
   * Cancel booking
   */
  cancel: (id: string) =>
    apiRequest(`/bookings/${id}/cancel`, {
      method: 'POST',
    }),
};

/**
 * Error handling helper
 */
export function isApiError(error: any): error is Error {
  return error instanceof Error;
}

export function getErrorMessage(error: any): string {
  if (isApiError(error)) {
    return error.message;
  }
  return 'An unknown error occurred';
}
